import { useState } from 'react';
import {
  listPhotos,
  patchProfile,
  photoContentTypeOf,
  sha256Hex,
  skipOnboardingStep,
  uploadPhotos,
  type Me,
  type PhotoContentType,
  type ProfilePatch,
} from '../../api/endpoints.js';
import { useAction, useApi, useIntentKey } from '../../api/hooks.js';
import { writeFlags } from '../../flow/machine.js';
import { goHome, goStep } from '../../flow/nav.js';
import { Failure } from '../../ui/kit.js';
import {
  FlowField,
  FlowScreen,
  FlowSub,
  FlowTitle,
  PrimaryButton,
  SecondaryButton,
} from '../../ui/flow.js';

/**
 * Mobile screens 15–17: employer/school, the photo batch, and the notifications
 * ask. The photos upload IMMEDIATELY here (mint → PUT to S3 → commit) rather
 * than deferring to a later tab — the mobile deferral exists because its picker
 * returns file URIs that survive locally; a browser's picked File objects do
 * not survive navigation, so deferring would lose them.
 */

export function ProfileExtraStep({ me, reloadMe }: { me: Me; reloadMe: () => void }) {
  const [employer, setEmployer] = useState(me.person.employer_name ?? '');
  const [school, setSchool] = useState(me.person.school_name ?? '');

  const save = useAction(
    async () => {
      const patch: ProfilePatch = {};
      // Only what changed, and only non-empty values — a deep-linked visit must
      // never blank a stored value (the mobile screen's own rule).
      if (employer.trim() !== (me.person.employer_name ?? '') && employer.trim().length > 0) {
        patch.employer_name = employer.trim();
      }
      if (school.trim() !== (me.person.school_name ?? '') && school.trim().length > 0) {
        patch.school_name = school.trim();
      }
      if (Object.keys(patch).length > 0) {
        await patchProfile(patch);
        return;
      }
      // Nothing entered, so the step is being walked past rather than answered.
      // The server cannot infer that from an absent employer_name — without a
      // recorded skip this screen is owed again on the next login, on every
      // device. Filling either field needs no skip: the field itself completes
      // the step.
      await skipOnboardingStep('profile_extra');
    },
    () => {
      reloadMe();
      goStep('photos', { replace: true });
    },
  );

  return (
    <FlowScreen
      progress={90}
      onBack={() => window.history.back()}
      footer={
        <PrimaryButton onClick={() => save.run()} disabled={save.pending}>
          {save.pending ? 'Saving…' : 'Continue'}
        </PrimaryButton>
      }
    >
      <FlowTitle>A little more about you</FlowTitle>
      <FlowSub>These details help protect your identity across platforms.</FlowSub>
      <div className="field-stack">
        <FlowField
          label={
            <>
              Employer <em>(Optional)</em>
            </>
          }
          value={employer}
          onChange={setEmployer}
          placeholder="Your company or employer"
          disabled={save.pending}
        />
        <FlowField
          label={
            <>
              School Attended <em>(Optional)</em>
            </>
          }
          value={school}
          onChange={setSchool}
          placeholder="Your school or university"
          disabled={save.pending}
        />
      </div>
      {save.error !== null && <Failure error={save.error} />}
    </FlowScreen>
  );
}

const PHOTO_LIMIT = 50;

/**
 * The photo grid, shared with the family-member loop.
 *
 * `subject` flips the COPY between "your face" and "their face";
 * `subjectPersonId` is what decides WHOSE SPACE the photos land in. Those were
 * the same prop until 2026-09-03, which is to say the second one did not
 * exist — see the note on it below.
 */
export function PhotoBatch({
  subject,
  subjectPersonId,
  onDone,
  onSkip,
}: {
  subject: 'you' | 'member';
  /**
   * WHOSE SPACE THESE PHOTOS GO IN, and until 2026-09-03 there was no way to
   * say.
   *
   * `subject` above flips the copy only. Without this, the family-member loop
   * rendered "Add up to 50 photos that feature Mel's face" and then filed them
   * against the MANAGER's person and the MANAGER's allowance — a 200 every
   * time, with nothing on any screen to reveal it. The per-member space is
   * what makes the right answer expressible; omitted still means the caller's
   * own person, so the self path is unchanged.
   */
  subjectPersonId?: string;
  onDone: () => void;
  onSkip: () => void;
}) {
  const [staged, setStaged] = useState<{ file: File; url: string }[]>([]);
  const [rejected, setRejected] = useState<string[]>([]);
  const [progress, setProgress] = useState<string | null>(null);
  const uploadKey = useIntentKey();
  // Photos upload immediately on the web (a browser File does not survive
  // navigation), so what is already stored counts: re-entering this step
  // with one photo uploaded used to read "0 / 50". One page at the limit is
  // exactly the number that matters here.
  // THAT SUBJECT'S stored count. The limit is per person now, so the
  // manager's full grid must not read as the member's.
  const stored = useApi(
    () => listPhotos(undefined, PHOTO_LIMIT, subjectPersonId),
    [subjectPersonId],
  );
  const storedCount = stored.data?.photos.length ?? 0;

  const upload = useAction(
    async () => {
      const accepted: { file: File; sha256: string; contentType: PhotoContentType }[] = [];
      for (const entry of staged) {
        const contentType = photoContentTypeOf(entry.file);
        if (contentType === null) continue;
        accepted.push({ file: entry.file, sha256: await sha256Hex(entry.file), contentType });
      }
      if (accepted.length === 0) throw new Error('no supported files selected');
      return uploadPhotos(
        accepted,
        uploadKey.key,
        (done, total) => setProgress(`${String(done)}/${String(total)} uploaded`),
        subjectPersonId,
      );
    },
    () => {
      uploadKey.next();
      setProgress(null);
      onDone();
    },
  );

  const addFiles = (incoming: File[]) => {
    const room = PHOTO_LIMIT - storedCount - staged.length;
    const refused: string[] = [];
    const accepted: { file: File; url: string }[] = [];
    for (const file of incoming) {
      if (photoContentTypeOf(file) === null) {
        refused.push(`${file.name} — unsupported type`);
        continue;
      }
      if (accepted.length < room) accepted.push({ file, url: URL.createObjectURL(file) });
    }
    setRejected(refused);
    setStaged([...staged, ...accepted]);
  };

  const count = storedCount + staged.length;

  return (
    <>
      <div className="photo-counter">
        <span>Photos Added</span>
        <span className="photo-counter-n">
          {count} / {PHOTO_LIMIT}
        </span>
      </div>
      <div className="mini-track">
        <div
          className="mini-fill"
          style={{ width: `${String(Math.min((count / PHOTO_LIMIT) * 100, 100))}%` }}
        />
      </div>

      <label className={upload.pending ? 'upload-tile disabled' : 'upload-tile'}>
        <input
          type="file"
          multiple
          accept="image/jpeg,image/heic,image/heif,.jpg,.jpeg,.heic,.heif"
          disabled={upload.pending || count >= PHOTO_LIMIT}
          onChange={(event) => {
            addFiles([...(event.target.files ?? [])]);
            event.target.value = '';
          }}
        />
        <span className="upload-tile-circle" aria-hidden="true">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 16V5m0 0l-4.5 4.5M12 5l4.5 4.5M5 19h14"
              stroke="#380E99"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <strong>Tap to upload photos</strong>
        <span className="flow-note" style={{ margin: 0 }}>
          JPG or HEIC, straight to secure storage
        </span>
      </label>

      {count > 0 && (
        <div className="photo-grid">
          {staged.map((entry, index) => (
            <button
              key={entry.url}
              type="button"
              className="photo-cell"
              title="Remove"
              disabled={upload.pending}
              onClick={() => {
                URL.revokeObjectURL(entry.url);
                setStaged(staged.filter((_, at) => at !== index));
              }}
            >
              <img src={entry.url} alt="" />
            </button>
          ))}
        </div>
      )}
      {rejected.length > 0 && <p className="flow-note">skipped: {rejected.join(', ')}</p>}
      {progress !== null && <p className="flow-verifying">{progress}</p>}
      {upload.error !== null && (
        <Failure
          error={upload.error}
          hint={
            {
              FILE_TOO_LARGE: 'Above the per-file ceiling.',
              LIMIT_EXCEEDED: 'Too many files for one batch, or the account photo ceiling.',
              UPLOAD_NOT_FOUND: 'A minted upload expired before the commit — try again.',
              S3_TRANSPORT: 'The uploads bucket does not allow PUT from this origin yet.',
              S3_REJECTED: 'Storage refused the signed upload — try the batch again.',
            }[upload.error.code]
          }
        />
      )}

      <div className="flow-footer-inline">
        {count > 0 ? (
          <PrimaryButton onClick={() => upload.run()} disabled={upload.pending}>
            {upload.pending
              ? 'Uploading…'
              : `Continue with ${String(count)} Photo${count === 1 ? '' : 's'}`}
          </PrimaryButton>
        ) : (
          <PrimaryButton onClick={onSkip} disabled={upload.pending}>
            Continue
          </PrimaryButton>
        )}
      </div>
      {subject === 'you' && count === 0 && (
        <p className="flow-note" style={{ textAlign: 'center' }}>
          You can add photos anytime from your Dashboard.
        </p>
      )}
    </>
  );
}

export function PhotosStep() {
  const skipPhotos = useAction(() => skipOnboardingStep('photos'), () =>
    goStep('notifications', { replace: true }),
  );

  return (
    <FlowScreen
      progress={94}
      onBack={() => window.history.back()}
      onClose={() => void finishTail()}
    >
      <FlowTitle>
        Upload your social media{' '}
        <br />
        photos to monitor
      </FlowTitle>
      <FlowSub>
        Add up to 50 photos that feature your face that you&rsquo;ve recently shared on social
        media. Include your profile pictures. We&rsquo;ll monitor for their use and abuse online.
      </FlowSub>
      <PhotoBatch
        subject="you"
        onDone={() => goStep('notifications', { replace: true })}
        onSkip={() => skipPhotos.run()}
      />
      {skipPhotos.error !== null && <p className="flow-error">{skipPhotos.error.message}</p>}
    </FlowScreen>
  );
}

/**
 * Where the tail of the flow exits: family setup for my_family, else Home.
 *
 * Records the notifications step as skipped server-side, and does so in the
 * GRANTED case as much as the declined one — deliberately. There is no /v1 route
 * for a web push token, so `notifications_enabled` can never become true from
 * this client, and without a recorded skip the screen is owed again at every
 * fresh login.
 *
 * BEST-EFFORT, unlike the other skips. This is the last screen of onboarding and
 * a failed request must not trap someone inside the flow; the cost of a miss is
 * being asked once more, which is plainly better than being unable to leave.
 */
async function finishTail(): Promise<void> {
  await skipOnboardingStep('notifications').catch(() => undefined);
  const flags = writeFlags({ checkpoint: null });
  if (flags.purpose === 'my_family' && !flags.familyComplete) {
    goStep('family-choice', { replace: true });
  } else {
    goHome();
  }
}

export function NotificationsStep() {
  const [busy, setBusy] = useState(false);

  const turnOn = () => {
    setBusy(true);
    // Permission errors must never block onboarding — mirror the mobile
    // screen's finally-block. There is no /v1 route for a web push token yet,
    // so the grant is the browser's alone.
    const request =
      'Notification' in window ? Notification.requestPermission() : Promise.resolve('denied');
    void Promise.resolve(request)
      .catch(() => undefined)
      .then(() => finishTail());
  };

  const skip = () => {
    setBusy(true);
    void finishTail();
  };

  return (
    <FlowScreen
      progress={97}
      footer={
        <>
          <PrimaryButton onClick={turnOn} disabled={busy}>
            Turn on notifications
          </PrimaryButton>
          <SecondaryButton onClick={skip} disabled={busy}>
            Skip
          </SecondaryButton>
        </>
      }
    >
      <div className="flow-center">
        <span className="bell-circle" aria-hidden="true">
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none">
            <path
              d="M6 10a6 6 0 1 1 12 0c0 4 1.5 5.5 2 6H4c.5-.5 2-2 2-6z"
              stroke="#380E99"
              strokeWidth="1.8"
              strokeLinejoin="round"
            />
            <path d="M10 19a2 2 0 0 0 4 0" stroke="#380E99" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </span>
        <FlowTitle>One last thing…don&rsquo;t miss alerts</FlowTitle>
        <FlowSub>
          Turn on notifications so we can alert you as soon as we discover any use of your likeness
          online.
        </FlowSub>
      </div>
    </FlowScreen>
  );
}
